import { ActionCenterTask, ActionCenterTaskType } from '@domain'
import { ActionCenterKeys } from '@services/actionCenterTasks.api'
import { PackConvertKeys } from '@services/packConverts.api'
import { PackConvert } from '@services/packConverts.types'
import { InfiniteData, QueryClient } from '@tanstack/react-query'

type PackConvertPage = PaginatedAPIRes<PackConvert[]>

export type PackConvertCacheSnapshot = {
	previousPackConvertsData: InfiniteData<PackConvertPage> | undefined
}

export const snapshotPackConvertCache = (
	queryClient: QueryClient,
	marketBasketId: string | number
): PackConvertCacheSnapshot => ({
	previousPackConvertsData: queryClient.getQueryData<InfiniteData<PackConvertPage>>(
		PackConvertKeys.list(marketBasketId)
	),
})

export const removePackConvertFromCache = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	purchaseMfgProdDmid: Id,
	candidateMfgProdDmid: Id
): void => {
	const compositeId = `${purchaseMfgProdDmid}_${candidateMfgProdDmid}`

	queryClient.setQueryData<InfiniteData<PackConvertPage> | undefined>(
		PackConvertKeys.list(marketBasketId),
		(old) => {
			if (!old) return old

			let removed = false
			const nextPages = old.pages.map((page) => {
				const nextData = page.data.filter((item) => {
					const matches = item.id === compositeId
					if (matches) removed = true
					return !matches
				})

				return {
					...page,
					data: nextData,
				}
			})

			if (!removed) return old

			nextPages[0] = {
				...nextPages[0],
				pagination: {
					...nextPages[0].pagination,
					total: Math.max(nextPages[0].pagination.total - 1, 0),
				},
			}

			return {
				...old,
				pages: nextPages,
			}
		}
	)
}

export const restorePackConvertCache = (
	queryClient: QueryClient,
	marketBasketId: string | number,
	snapshot: PackConvertCacheSnapshot
): void => {
	if (!snapshot.previousPackConvertsData) return

	queryClient.setQueryData(PackConvertKeys.list(marketBasketId), snapshot.previousPackConvertsData)
}

export const updatePackConvertActionCenterCache = (
	queryClient: QueryClient,
	marketBasketId: Id
): void => {
	const actionCenterKey = ActionCenterKeys.tasks(marketBasketId)

	queryClient.setQueryData<ActionCenterTask[] | undefined>(actionCenterKey, (old) => {
		if (!old) return old

		return old.map((task) => {
			if (task.task === ActionCenterTaskType.PACK_CONVERSION) {
				return {
					...task,
					completed: true,
				}
			}

			if (task.task === ActionCenterTaskType.DIRECT_DEALS) {
				return {
					...task,
					isLoading: true,
				}
			}

			return task
		})
	})
}
